import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import React, { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { AVAILABLE_ARR_LANGS } from '@/common/constants/language'
import { device, size } from '@/common/constants/device'
import { HASH_KEY, TOKEN_KEY, USER_LANG_KEY } from '@/common/constants/cookies'
import { authApi, userApi } from '@/common/services/api'
import Icon from '@/components/common/Icon'
import Row from '@/components/common/Row'
import Button from '@/components/ui/Button'
import Divider from '@/components/ui/Divider'
import { useIsModalVisible, useResizeHandler } from '@/hooks'
import {
  clear,
  fetchUserInfo,
  setUserRedirectLoginPath,
} from '@/store/userSlice'

import HeaderUser from './HeaderUser'
import HeaderBalance from './HeaderBalance'
import HeaderNavigation from './HeaderNavigation'
import HeaderSubscription from './HeaderSubscription'
import BaseLink from '@/components/common/BaseLink'
import { REDIRECT_URL } from '@/common/constants/redirects'
import getConfig from 'next/config'
import { userAnalytics } from '@/common/services/userAnalytics'
import dynamic from 'next/dynamic'
import userLanguage from '@/common/services/userLanguage'
import baseLogger from '@/common/services/BaseLogger'
import Link from 'next/link'
import LoaderHexagon from '@/components/LoaderHexagon'
import { signOut } from 'next-auth/react'
import baseErrorPlaceholder from '@/components/common/BaseErrorPlaceholder'
import { ErrorBoundary } from 'react-error-boundary'
import { userAmplitudeAnalytics } from '@/common/services/userAmplitudeAnalytics'
import { ANALYTIC_HEADER } from '@/common/constants/analytics'
import { FEATURE_REQUEST_MODAL } from '@/common/constants/modals'
import ProfileFeatureRequestModal from '@/components/Profile/ProfileFeatureRequestModal'
import { useTranslations } from 'next-intl'

const HeaderLangs = dynamic(() => import('./HeaderLangs'))

const Layout = styled.header`
  position: fixed;
  left: 0;
  top: 0;
  z-index: 50;
  width: 100%;
  max-height: 100vh;
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  width: 100%;
  background-color: var(--white);
  min-height: var(--header-height);
  border-bottom: 1px solid rgba(224, 225, 226, 1);

  @media ${device.tablet} {
    padding: 8px 24px;
  }
  @media ${device.laptop} {
    padding: 11px 40px;
  }
`

const HeaderNavWrap = styled.div`
  /* display: none;
  @media ${device.laptop} {
    display: block;
  } */
`

const Logo = styled(BaseLink)`
  width: 79px;
  height: 32px;
  background-image: url('/_next_static/logo.png');
  background-size: cover;
  background-repeat: no-repeat;

  @media ${device.tablet} {
    margin-right: 40px;
  }

  @media ${device.laptop} {
    width: 97px;
    min-width: 97px;
    height: 40px;
  }
`
const UserDropdownsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, auto);
  align-items: center;
  gap: 32px;
  @media ${device.toLaptopL} {
    display: none;
  }
`
const AuthButtonsWrapper = styled(Row)`
  gap: 4px;
  @media ${device.toLaptopL} {
    display: none;
  }
`
const SidebarButton = styled.button`
  cursor: pointer;
  background: transparent;
  @media ${device.laptopL} {
    display: none;
  }
`
const Sidebar = styled.div`
  padding: 24px;
  background-color: var(--white);
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  @media ${device.tablet} {
    max-width: 360px;
    margin: 12px 24px 0 auto;
    padding: 24px 16px;
    box-shadow: 0px 0px 0px 0px rgba(27, 27, 68, 0.08),
      -1px 6px 13px 0px rgba(27, 27, 68, 0.08),
      -2px 24px 24px 0px rgba(27, 27, 68, 0.07),
      -5px 54px 32px 0px rgba(27, 27, 68, 0.04),
      -9px 96px 38px 0px rgba(27, 27, 68, 0.01),
      -13px 149px 42px 0px rgba(27, 27, 68, 0);
    border-radius: 16px;
  }
`
const Langs = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
`
const Lang = styled.button`
  font-weight: 400;
  font-size: 1.125em;
  line-height: 140%;
  color: ${({ active }) => (active ? 'var(--yellow)' : '#6e7278')};
  background: none;
  width: fit-content;
  cursor: ${(props) => (props.active ? 'default' : 'pointer')};
`
const DividerWrapper = styled.div`
  background-color: var(--white);
  @media ${device.tablet} {
    display: none;
  }
`
const Wrap = styled.div`
  height: 50px;
  display: flex;
  justify-content: center;
`
const HeaderLoadingSpinner = styled.div`
  width: 100px;
  margin-left: auto;
  @media ${device.toLaptopL} {
    display: none;
  }
`

const translations = {
  var1_control: {
    cta: 'landingVar1Cta',
  },
  var2: {
    cta: 'landingVar2Cta',
  },
  var3: {
    cta: 'landingVar3Cta',
  },
  var4: {
    cta: 'landingVar4Cta',
  },
  var5: {
    cta: 'landingVar5Cta',
  },
  var6: {
    cta: 'landingVar6Cta',
  },
  var7: {
    cta: 'landingVar7Cta',
  },
  var8: {
    cta: 'landingVar8Cta',
  },
  var9: {
    cta: 'landingVar9Cta',
  },
}

const Header = memo(({ experimentKey }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSidebarVisible, setIsSidebarVisible] = useState(false)
  const router = useRouter()
  const languagesLinks = useSelector((state) => state.language.languagesLinks)
  const { locale } = router
  const t = useTranslations('common')

  const [currentHash, setCurrentHash] = useState('')

  const dispatch = useDispatch()
  const { user, subscription, referralBalance, status, userRedirectPath } =
    useSelector((state) => state.user)
  const isVisibleFeatureRequestModal = useIsModalVisible(FEATURE_REQUEST_MODAL)

  useResizeHandler(() => {
    if (window.innerWidth > size.laptopL) {
      setIsSidebarVisible(false)
    }
  })

  useEffect(() => {
    const handleHashChange = () => {
      setCurrentHash(window.location.hash)
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [])

  const languagePath = (language) => {
    if (languagesLinks && Object.keys(languagesLinks).length) {
      const correctQuery = languagesLinks[language]
        ? languagesLinks[language].slug
        : router.query.id
      const currentPath = router.asPath
      return currentPath.replace(router.query.id, correctQuery)
    }
    return router.pathname === '/' ? currentHash : router.asPath
  }

  // mounted
  useEffect(() => {
    userLanguage.setLanguage(locale)

    const fetchToken = async () => {
      try {
        setIsLoading(true)

        const res = await authApi.token.getByHash({
          headers: {
            ['X-Hash']: getCookie(HASH_KEY),
          },
        })

        setCookie(TOKEN_KEY, res.token)

        dispatch(fetchUserInfo())
      } catch (e) {
        deleteCookie(HASH_KEY)
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    }

    if (!getCookie(TOKEN_KEY) || !getCookie(HASH_KEY)) return

    if (getCookie(TOKEN_KEY)) {
      dispatch(fetchUserInfo())
    } else if (getCookie(HASH_KEY)) {
      fetchToken()
    }
  }, [])

  useEffect(() => {
    let path = router.asPath

    if (path.includes(REDIRECT_URL)) {
      const query = router.asPath.split('?')[1]
      const urlSearchParams = new URLSearchParams(query)
      const params = Object.fromEntries(urlSearchParams.entries())

      path = params[REDIRECT_URL]
    }

    const encodePath = encodeURIComponent(path)
    dispatch(setUserRedirectLoginPath(encodePath))
  }, [router.isReady])

  const loginPathWithRedirect = useMemo(() => {
    return userRedirectPath
      ? `/login?${REDIRECT_URL}=${userRedirectPath}`
      : '/login'
  }, [userRedirectPath])

  const isUserAuth = useMemo(
    () => user && subscription && referralBalance,
    [user, subscription, referralBalance]
  )
  const isUserInfoLoading = useMemo(
    () => isLoading || status === 'loading',
    [status, isLoading]
  )

  const currentLanguage = useMemo(() => locale, [locale])

  const onButtonClick = (name) => {
    userAmplitudeAnalytics.trackEvent(`${ANALYTIC_HEADER}_${name}_clicked`)
  }

  const disableRedirect = (e) => {
    onButtonClick('login')

    if (router.pathname === '/login') {
      e.preventDefault()
    }
  }

  const onSidebarButtonClick = () => setIsSidebarVisible(!isSidebarVisible)
  const onLogoutButtonClick = async () => {
    userAnalytics.track(['setUserId', user.uuid])
    try {
      await userApi.logout()
    } catch (e) {
      console.log(e)
    } finally {
      dispatch(clear())
      deleteCookie(TOKEN_KEY)
      deleteCookie(HASH_KEY, {
        domain: `.${getConfig().publicRuntimeConfig.baseDomain}`,
      })
      signOut({
        callbackUrl: '/',
      })
    }
  }

  const changeCurrentLanguage = async (e) => {
    localStorage.setItem('currentHash', currentHash)
    const currentUrl = window.location.href
    const queryParams = new URLSearchParams(router.query)
    const language = e.target.value
    const newUrl = new URL(currentUrl)
    newUrl.search = queryParams.toString()
    setCookie(USER_LANG_KEY, language)
    userLanguage.setLanguage(language)
    if (!getCookie(TOKEN_KEY)) return

    try {
      await userApi.changeUserLanguage({
        data: {
          language,
        },
      })
    } catch (e) {
      baseLogger.logger.error(e, 'failed set language')
    }
  }

  const key = translations[experimentKey]

  return (
    <Layout>
      {isVisibleFeatureRequestModal && <ProfileFeatureRequestModal />}
      <HeaderWrapper>
        <Logo href={user ? '/dashboard' : '/'} isChangePath={false} />
        <HeaderNavWrap>
          <HeaderNavigation isHeader />
        </HeaderNavWrap>
        {isUserInfoLoading ? (
          <HeaderLoadingSpinner>
            <LoaderHexagon height={11} width={20} small />
          </HeaderLoadingSpinner>
        ) : isUserAuth ? (
          <UserDropdownsWrapper className="ml-auto">
            <ErrorBoundary FallbackComponent={baseErrorPlaceholder}>
              <HeaderSubscription isDropdown />
            </ErrorBoundary>
            <HeaderBalance isDropdown />
            <HeaderLangs
              isDropdown
              languagesLinks={languagesLinks}
              key="no-user-langs"
            />
            <HeaderUser isDropdown />
          </UserDropdownsWrapper>
        ) : (
          <Row className="ml-auto" alignItems="center">
            <HeaderLangs
              className="mr-4"
              key="user-langs"
              isDropdown
              hideToLaptopL
              languagesLinks={languagesLinks}
            />
            <AuthButtonsWrapper alignItems="center">
              <BaseLink href={loginPathWithRedirect} isChangePath={false}>
                <Button
                  onClick={() => onButtonClick('login')}
                  block
                  className="mr-2"
                >
                  {t('login')}
                </Button>
              </BaseLink>
              <BaseLink href="/register" isChangePath={false}>
                <Button onClick={() => onButtonClick('reg')} block filled>
                  {t(key?.cta || 'registration')}
                </Button>
              </BaseLink>
            </AuthButtonsWrapper>
          </Row>
        )}
        <SidebarButton className="ml-auto" onClick={onSidebarButtonClick}>
          <Icon
            icon={isSidebarVisible ? 'close' : 'hamburger-menu'}
            size="28"
          />
        </SidebarButton>
      </HeaderWrapper>

      {isSidebarVisible && (
        <>
          <DividerWrapper className="px-4">
            <Divider />
          </DividerWrapper>

          <Sidebar>
            <HeaderNavigation
              isVisibleNewSearch={subscription?.is_new_search_available}
              className="mb-4"
            />
            <DividerWrapper className="mb-4">
              <Divider />
            </DividerWrapper>
            <Langs className="mb-4">
              {AVAILABLE_ARR_LANGS.map((el) =>
                currentLanguage === el ? (
                  <Lang
                    key={el}
                    active={currentLanguage === el}
                    className="mb-3"
                    value={el}
                    onClick={changeCurrentLanguage}
                  >
                    {el.toUpperCase()}
                  </Lang>
                ) : (
                  <Link
                    key={el}
                    href={`${languagePath(el)}`}
                    locale={el}
                    scroll={false}
                  >
                    <Lang
                      active={currentLanguage === el}
                      className="mb-3"
                      value={el}
                      onClick={changeCurrentLanguage}
                    >
                      {el.toUpperCase()}
                    </Lang>
                  </Link>
                )
              )}
            </Langs>
            <Divider className="mb-4" />
            {isUserInfoLoading ? (
              <Wrap>
                <LoaderHexagon className="mx-auto" small />
              </Wrap>
            ) : isUserAuth ? (
              <>
                <HeaderSubscription className="mb-4" />
                <HeaderBalance className="mb-4" />
                <HeaderUser className="mb-4" />
                <Button className="ml-auto" onClick={onLogoutButtonClick}>
                  {t('logout')}
                </Button>
              </>
            ) : (
              <Row alignItems="center" gap="8">
                <BaseLink href={loginPathWithRedirect} isChangePath={false}>
                  <Button block className="mr-2" onClick={disableRedirect}>
                    {t('login')}
                  </Button>
                </BaseLink>
                <BaseLink href="/register" isChangePath={false}>
                  <Button onClick={() => onButtonClick('reg')} block filled>
                    {t('registration')}
                  </Button>
                </BaseLink>
              </Row>
            )}
          </Sidebar>
        </>
      )}
    </Layout>
  )
})

export default Header
